import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { ProductCard } from '../../components/base/ProductCard';
import { products } from '../../mocks/products';

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('cat');
  const searchQuery = searchParams.get('search');

  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500000]);
  const [selectedKarat, setSelectedKarat] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);

  const karatOptions = [14, 18, 22, 24];

  let filteredProducts = [...products];

  if (categoryParam) {
    filteredProducts = filteredProducts.filter(p => p.category === categoryParam);
  }

  if (searchQuery) {
    filteredProducts = filteredProducts.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  filteredProducts = filteredProducts.filter(
    p => p.price >= priceRange[0] && p.price <= priceRange[1]
  );

  if (selectedKarat.length > 0) {
    filteredProducts = filteredProducts.filter(p => selectedKarat.includes(p.karat));
  }

  switch (sortBy) {
    case 'price-asc':
      filteredProducts.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      filteredProducts.sort((a, b) => b.price - a.price);
      break;
    case 'newest':
      filteredProducts.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
      break;
  }

  const getCategoryName = () => {
    switch (categoryParam) {
      case 'ceyrek-altin':
        return 'Çeyrek Altın';
      case 'yarim-altin':
        return 'Yarım Altın';
      case 'tam-altin':
        return 'Tam Altın';
      case 'gram-altin':
        return 'Gram Altın';
      case 'bilezik':
        return 'Bilezik & Kelepçe';
      case 'kolye':
        return 'Kolye & Yüzük';
      case 'set':
        return 'Setler';
      case 'ziynet':
        return 'Ziynet Altını';
      default:
        return 'Tüm Ürünler';
    }
  };

  const toggleKarat = (karat: number) => {
    setSelectedKarat(prev =>
      prev.includes(karat) ? prev.filter(k => k !== karat) : [...prev, karat]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      <Header />

      <div className="bg-gradient-to-r from-gold-50 to-amber-50 py-12">
        <div className="container mx-auto px-4">
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/" className="hover:text-gold-600 cursor-pointer">
              Anasayfa
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800 font-medium">{getCategoryName()}</span>
          </nav>
          <h1 className="text-4xl font-bold text-gray-800 mb-3">{getCategoryName()}</h1>
          <p className="text-gray-600">
            Sertifikalı ve güvenilir altın ürünlerimizi keşfedin
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Filtreler</h2>
                <button
                  onClick={() => {
                    setPriceRange([0, 500000]);
                    setSelectedKarat([]);
                  }}
                  className="text-sm text-gold-600 hover:text-gold-700 cursor-pointer whitespace-nowrap"
                >
                  Temizle
                </button>
              </div>

              <div className="mb-6">
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Fiyat Aralığı</h3>
                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max="500000"
                    step="5000"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full accent-gold-600"
                  />
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>0 ₺</span>
                    <span>{priceRange[1].toLocaleString('tr-TR')} ₺</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Ayar</h3>
                <div className="space-y-2">
                  {karatOptions.map(karat => (
                    <label key={karat} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedKarat.includes(karat)}
                        onChange={() => toggleKarat(karat)}
                        className="w-4 h-4 accent-gold-600 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{karat} Ayar</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Stok Durumu</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-gold-600 cursor-pointer" />
                  <span className="text-sm text-gray-700">Sadece Stokta Olanlar</span>
                </label>
              </div>
            </div>
          </aside>

          <main className="flex-1">
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  <strong>{filteredProducts.length}</strong> ürün bulundu
                </p>

                <div className="flex items-center gap-4 w-full sm:w-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-filter-line"></i>
                    Filtreler
                  </button>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 cursor-pointer"
                  >
                    <option value="featured">Öne Çıkanlar</option>
                    <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                    <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                    <option value="newest">Yeni Gelenler</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Ürün Bulunamadı
                </h3>
                <p className="text-gray-600 mb-6">
                  Aradığınız kriterlere uygun ürün bulunamadı. Filtreleri değiştirmeyi deneyin.
                </p>
                <Link
                  to="/kategori"
                  className="inline-block bg-gold-600 text-white px-6 py-3 rounded-lg hover:bg-gold-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Tüm Ürünleri Gör
                </Link>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
